import {
  checkpoints,
  opIndexAt,
  scheduleReplay,
  type KeystrokeLog,
  type ReplaySchedule,
} from '@coderaw/log'
import { clamp, latestCheckpoint, speedOptions, type SpeedChoice } from './replayState'

/** A hidden tab pauses rAF; on return the gap is clamped so the replay does not jump. */
export const MAX_FRAME_DELTA_MS = 100
export const CHECKPOINT_GAP_MS = 250

export type TransportOptions = {
  readonly log: KeystrokeLog
  readonly parses: (doc: string) => boolean
  readonly autoplay?: boolean
}

export type Transport = {
  readonly schedule: () => ReplaySchedule
  readonly elapsed: () => number
  readonly playing: () => boolean
  /** Ops fired so far. */
  readonly opIndex: () => number
  /** The checkpoint for the current position, or 0 when no doc parses yet. */
  readonly checkpoint: () => number
  readonly setPlaying: (next: boolean) => void
  /** Play/pause; playing from the end starts over. */
  readonly toggle: () => void
  readonly restart: () => void
  readonly scrub: (ms: number) => void
  readonly setSpeed: (choice: SpeedChoice) => void
  /** Advances by the wall-clock delta; returns true while the replay still runs. */
  readonly tick: (now: number) => boolean
  /** Forget the last tick so the next one does not count time spent hidden. */
  readonly resync: () => void
}

/** Replay time, speed and checkpoints, with no DOM. */
export const createTransport = (options: TransportOptions): Transport => {
  const { log, parses } = options
  let schedule = scheduleReplay(log)
  let cps = checkpoints(log, parses, schedule, CHECKPOINT_GAP_MS)
  let elapsed = 0
  let playing = options.autoplay ?? true
  let lastNow: number | null = null

  const atEnd = (): boolean => elapsed >= schedule.totalMs
  const setPlaying = (next: boolean): void => {
    playing = next
    lastNow = null
  }

  return {
    schedule: () => schedule,
    elapsed: () => elapsed,
    playing: () => playing,
    opIndex: () => opIndexAt(schedule, elapsed),
    checkpoint: () => latestCheckpoint(cps, opIndexAt(schedule, elapsed)),
    setPlaying,
    toggle: () => {
      if (!playing && atEnd()) elapsed = 0
      setPlaying(!playing)
    },
    restart: () => {
      elapsed = 0
      setPlaying(true)
    },
    scrub: (ms) => {
      elapsed = clamp(ms, 0, schedule.totalMs)
    },
    setSpeed: (choice) => {
      const fraction = schedule.totalMs > 0 ? elapsed / schedule.totalMs : 0
      schedule = scheduleReplay(log, speedOptions(choice))
      cps = checkpoints(log, parses, schedule, CHECKPOINT_GAP_MS)
      elapsed = fraction * schedule.totalMs
    },
    tick: (now) => {
      if (!playing) return false
      if (lastNow !== null) {
        const delta = Math.min(MAX_FRAME_DELTA_MS, Math.max(0, now - lastNow))
        elapsed = clamp(elapsed + delta, 0, schedule.totalMs)
      }
      lastNow = now
      if (atEnd()) setPlaying(false)
      return playing
    },
    resync: () => {
      lastNow = null
    },
  }
}
